import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SWISSCHEM_LOGO from './logoBase64';

const COMPANY = {
  name: 'SWISS CHEM INTERNATIONAL',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad.',
  phone: '041-2611464, 0300-7671404',
  email: 'sales@swisschem.pk',
};

const NAVY = [31, 58, 95];       // dark navy header
const NAVY_LIGHT = [40, 72, 115];

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtQty = (n) => {
  const v = parseFloat(n) || 0;
  return v % 1 === 0 ? String(v) : v.toFixed(2);
};

/**
 * Outward Gate Pass — matches the SwissChem template layout
 * @param {object} gp - full gate pass object (with items[]) from getGatePassById
 */
export const generateGatePassPdf = (gp) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 12;
  const rightEdge = pageWidth - marginX;
  const contentW = pageWidth - marginX * 2;

  // ═══ HEADER ═══
  // Company logo on the left (embedded, replaces the old navy LOGO box)
  const logoBoxW = 42;
  const headerH = 16;
  const logoImgW = logoBoxW;
  const logoImgH = logoImgW / 4.72;                 // preserve aspect ratio ≈ 8.9mm
  const logoImgY = 12 + (headerH - logoImgH) / 2;   // vertically centered in header band
  doc.addImage(SWISSCHEM_LOGO, 'PNG', marginX, logoImgY, logoImgW, logoImgH, undefined, 'FAST');

  // Company name (navy text) to the right of logo
  doc.setTextColor(...NAVY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(21);
  doc.text(COMPANY.name, marginX + logoBoxW + (contentW - logoBoxW) / 2, 21, { align: 'center' });

  // Title "OUTWARD GATE PASS" italic under company name
  doc.setFont('helvetica', 'bolditalic');
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  const titleLabel = gp.direction === 'inward' ? 'INWARD  GATE  PASS' : 'OUTWARD  GATE  PASS';
  doc.text(titleLabel, marginX + logoBoxW + (contentW - logoBoxW) / 2, 27, { align: 'center' });

  // Thick underline under whole header
  let y = 31;
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.8);
  doc.line(marginX, y, rightEdge, y);
  doc.setLineWidth(0.2);

  // ═══ INFO BLOCK (2 columns) ═══
  y += 8;
  const labelColor = NAVY;
  const valueColor = [40, 40, 40];
  const leftLabelX = marginX;
  const leftValX = marginX + 34;
  const leftLineEnd = marginX + contentW / 2 - 8;
  const rightLabelX = marginX + contentW / 2 + 4;
  const rightValX = rightLabelX + 26;
  const rightLineEnd = rightEdge;

  const rowGap = 8.5;

  // Left column: Company Name, Address, Contact Person, Contact Number, PO
  const leftRows = [
    ['Company Name :', gp.party_name || ''],
    ['Address :', gp.party_address || ''],
    ['Contact Person :', gp.contact_person || ''],
    ['Contact Number :', gp.party_phone || ''],
    ['PO :', gp.po_number || ''],
  ];
  // Right column: Date, GP #, Vehicle #, Driver Name, Contact #
  const rightRows = [
    ['Date :', formatDate(gp.gp_date)],
    ['GP # :', gp.gp_no || ''],
    ['Vehicle # :', gp.vehicle_no || ''],
    ['Driver Name :', gp.rider_name || ''],
    ['Contact # :', gp.rider_phone || ''],
  ];

  doc.setFontSize(9.5);
  for (let i = 0; i < 5; i++) {
    const ry = y + i * rowGap;
    // Left
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...labelColor);
    doc.text(leftRows[i][0], leftLabelX, ry);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...valueColor);
    if (leftRows[i][1]) doc.text(String(leftRows[i][1]), leftValX, ry);
    doc.setDrawColor(150, 150, 150);
    doc.line(leftValX, ry + 1.5, leftLineEnd, ry + 1.5);

    // Right
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...labelColor);
    doc.text(rightRows[i][0], rightLabelX, ry);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...valueColor);
    if (rightRows[i][1]) doc.text(String(rightRows[i][1]), rightValX, ry);
    doc.line(rightValX, ry + 1.5, rightLineEnd, ry + 1.5);
  }

  y += 5 * rowGap + 3;

  // ═══ ITEMS TABLE ═══
  const items = gp.items || [];
  let totalQty = 0;
  const rows = items.map((it, idx) => {
    const q = parseFloat(it.quantity) || 0;
    totalQty += q;
    return [
      idx + 1,
      it.product_code || '',
      it.product_name || '',
      it.batch_no || '',
      `${fmtQty(q)} ${it.unit || ''}`.trim(),
      it.packaging_qty != null ? String(it.packaging_qty) : '',
      it.remarks || '',
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['Sr.#', 'Item Id', 'Item Description', 'Batch #', 'Qty.', 'Packing Size', 'Remarks']],
    body: rows.length ? rows : [['', '', 'No items', '', '', '', '']],
    theme: 'grid',
    headStyles: {
      fillColor: NAVY_LIGHT, textColor: [255, 255, 255], fontStyle: 'bold',
      fontSize: 9, halign: 'center', valign: 'middle', lineColor: [255, 255, 255], lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 9, textColor: [30, 30, 30], lineColor: [180, 180, 180], lineWidth: 0.2,
      minCellHeight: 8, valign: 'middle',
    },
    columnStyles: {
      0: { cellWidth: 14, halign: 'center' },
      1: { cellWidth: 24, halign: 'center' },
      2: { cellWidth: 'auto', halign: 'left' },
      3: { cellWidth: 28, halign: 'center' },
      4: { cellWidth: 24, halign: 'center' },
      5: { cellWidth: 28, halign: 'center' },
      6: { cellWidth: 28, halign: 'center' },
    },
    margin: { left: marginX, right: marginX },
    foot: [[
      { content: 'TOTAL', colSpan: 4, styles: { halign: 'center', fontStyle: 'bold', fillColor: [240, 240, 240], textColor: NAVY } },
      { content: fmtQty(totalQty), styles: { halign: 'center', fontStyle: 'bold', fillColor: [240, 240, 240], textColor: [30, 30, 30] } },
      { content: '', styles: { fillColor: [240, 240, 240] } },
      { content: '', styles: { fillColor: [240, 240, 240] } },
    ]],
    footStyles: { lineColor: [180, 180, 180], lineWidth: 0.2 },
  });

  let afterTable = doc.lastAutoTable.finalY;

  // ═══ RECEIVED BY ═══
  const receivedY = afterTable + 40;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...NAVY);
  doc.text('Received By', rightEdge - 20, receivedY, { align: 'center' });

  // ═══ FOOTER ═══
  // system generated note
  const noteY = pageHeight - 40;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.2);
  doc.line(marginX, noteY - 6, rightEdge, noteY - 6);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(110, 110, 110);
  doc.text('This is a system generated document and does not require any signature or stamp.', pageWidth / 2, noteY, { align: 'center' });

  // navy footer bar with address
  const footBarH = 8;
  const footBarY = pageHeight - 22;
  doc.setFillColor(...NAVY);
  doc.rect(marginX, footBarY, contentW, footBarH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    `Address : ${COMPANY.address} Phone: ${COMPANY.phone} Email : ${COMPANY.email}`,
    pageWidth / 2, footBarY + footBarH / 2 + 1.5, { align: 'center' }
  );

  doc.save(`${gp.gp_no || 'Gate_Pass'}.pdf`);
};